import React, { useEffect, useState } from 'react';
import {
  BarChart3,
  CheckCircle,
  Clock,
  AlertTriangle,
  Globe,
  TrendingUp,
  Loader2,
  RefreshCw,
  ArrowRight,
  Edit3,
  Send,
  Eye,
} from 'lucide-react';
import { adminFetch } from '../../lib/api';
import { QAStatusBadge, CHANGE_TYPE_ICONS } from './QAStatusBadge';
import type { QAStatus } from './QAStatusBadge';
import { CHAPTER_TITLES_DE } from '@shared/chapters';

const CHAPTER_NAMES = CHAPTER_TITLES_DE;

interface DashboardData {
  totalQuestions: number;
  statusCounts: Record<string, number>;
  translationErrors: number;
  outdatedTranslations: number;
  chapterProgress: Record<
    number,
    { total: number; live: number; approved: number; draft: number; in_review: number }
  >;
  recentChanges: any[];
}

export const QADashboard: React.FC<{ onNavigateToQuestion?: (id: string) => void }> = ({
  onNavigateToQuestion,
}) => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const resp = await adminFetch('/api/admin/content-qa/dashboard');
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setData(await resp.json());
      setError('');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: 'center' }}>
        <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="alert alert-danger">
        <AlertTriangle size={16} /> Fehler beim Laden: {error}
        <button
          className="btn btn-sm btn-secondary"
          onClick={loadDashboard}
          style={{ marginLeft: 12 }}
        >
          <RefreshCw size={12} /> Erneut versuchen
        </button>
      </div>
    );
  }

  const {
    totalQuestions,
    statusCounts,
    translationErrors,
    outdatedTranslations,
    chapterProgress,
    recentChanges,
  } = data;
  const reviewed = (statusCounts.approved || 0) + (statusCounts.live || 0);
  const reviewedPct = totalQuestions > 0 ? Math.round((reviewed / totalQuestions) * 100) : 0;
  const pending =
    (statusCounts.draft || 0) +
    (statusCounts.in_progress || 0) +
    (statusCounts.in_review || 0) +
    (statusCounts.updated || 0);

  return (
    <div>
      {/* KPI Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
          gap: 12,
          marginBottom: 24,
        }}
      >
        <KPICard
          label="Fragen gesamt"
          value={totalQuestions}
          color="#3B82F6"
          icon={<BarChart3 size={18} />}
        />
        <KPICard
          label="Geprüft / Live"
          value={`${reviewed}`}
          subtitle={`${reviewedPct}%`}
          color="#22C55E"
          icon={<CheckCircle size={18} />}
        />
        <KPICard label="Ausstehend" value={pending} color="#F59E0B" icon={<Clock size={18} />} />
        <KPICard
          label="Übersetzungsfehler"
          value={translationErrors}
          color={translationErrors > 0 ? '#EF4444' : '#6B7280'}
          icon={<AlertTriangle size={18} />}
        />
        <KPICard
          label="Veraltet (i18n)"
          value={outdatedTranslations}
          color={outdatedTranslations > 0 ? '#F97316' : '#6B7280'}
          icon={<Globe size={18} />}
        />
      </div>

      {/* Chapter Progress */}
      <div className="card" style={{ padding: 20, marginBottom: 24 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
          <h3
            style={{ fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <TrendingUp size={16} /> Kapitel-Fortschritt
          </h3>
          <button className="btn btn-ghost btn-sm" onClick={loadDashboard}>
            <RefreshCw size={12} /> Aktualisieren
          </button>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 10,
          }}
        >
          {Object.entries(chapterProgress).map(([chId, ch]) => {
            const livePct = ch.total > 0 ? Math.round((ch.live / ch.total) * 100) : 0;
            const approvedPct = ch.total > 0 ? Math.round((ch.approved / ch.total) * 100) : 0;
            const allGood = ch.live === ch.total && ch.total > 0;
            return (
              <div
                key={chId}
                style={{
                  padding: '10px 14px',
                  borderRadius: 10,
                  border: `1px solid ${allGood ? 'rgba(34,197,94,0.3)' : 'var(--border)'}`,
                  background: allGood ? 'rgba(34,197,94,0.04)' : 'var(--surface)',
                }}
              >
                <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>Kap. {chId}</span>
                  <span className="text-xs text-muted">{ch.total} Fragen</span>
                </div>
                <div className="text-xs text-muted" style={{ marginBottom: 4 }}>
                  {CHAPTER_NAMES[parseInt(chId)] || ''}
                </div>
                {/* Progress bar */}
                <div
                  style={{
                    display: 'flex',
                    height: 6,
                    borderRadius: 3,
                    overflow: 'hidden',
                    background: 'var(--bg-secondary)',
                    marginBottom: 6,
                  }}
                >
                  <div
                    style={{
                      width: `${livePct}%`,
                      background: '#059669',
                      transition: 'width 0.3s',
                    }}
                  />
                  <div
                    style={{
                      width: `${approvedPct}%`,
                      background: '#22C55E',
                      transition: 'width 0.3s',
                    }}
                  />
                </div>
                <div className="flex gap-3 text-xs text-muted">
                  <span style={{ color: '#059669' }}>✅ {ch.live}</span>
                  <span style={{ color: '#22C55E' }}>✓ {ch.approved}</span>
                  {ch.in_review > 0 && <span style={{ color: '#F59E0B' }}>⏳ {ch.in_review}</span>}
                  {ch.draft > 0 && <span style={{ color: '#6B7280' }}>📝 {ch.draft}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Changes */}
      <div className="card" style={{ padding: 20 }}>
        <h3
          style={{
            fontSize: 14,
            fontWeight: 700,
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Clock size={16} /> Letzte Änderungen
        </h3>
        {recentChanges.length === 0 ? (
          <div className="text-sm text-muted" style={{ textAlign: 'center', padding: 30 }}>
            Noch keine Änderungen über das QA-System.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                  <th style={{ padding: '8px 6px', fontWeight: 700 }}>Kap.</th>
                  <th style={{ padding: '8px 6px', fontWeight: 700 }}>Fragetext</th>
                  <th style={{ padding: '8px 6px', fontWeight: 700 }}>Aktion</th>
                  <th style={{ padding: '8px 6px', fontWeight: 700 }}>Von</th>
                  <th style={{ padding: '8px 6px', fontWeight: 700 }}>Zeit</th>
                  <th style={{ padding: '8px 6px', fontWeight: 700 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentChanges.map((change, i) => {
                  const typeConfig =
                    CHANGE_TYPE_ICONS[change.change_type] || CHANGE_TYPE_ICONS.edit;
                  const timeAgo = formatTimeAgo(change.created_at);
                  return (
                    <tr
                      key={i}
                      style={{
                        borderBottom: '1px solid var(--border)',
                        cursor: onNavigateToQuestion ? 'pointer' : 'default',
                      }}
                      onClick={() => onNavigateToQuestion?.(change.question_id)}
                    >
                      <td style={{ padding: '8px 6px' }}>
                        <span className="badge badge-neutral" style={{ fontSize: 10 }}>
                          {change.chapter_id}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: '8px 6px',
                          maxWidth: 250,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {change.question_text_preview || change.question_id.substring(0, 8)}
                      </td>
                      <td style={{ padding: '8px 6px' }}>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            color: typeConfig.color,
                            fontWeight: 600,
                            fontSize: 11,
                          }}
                        >
                          {typeConfig.icon} {typeConfig.label}
                        </span>
                      </td>
                      <td style={{ padding: '8px 6px' }} className="text-muted">
                        {change.changed_by}
                      </td>
                      <td style={{ padding: '8px 6px' }} className="text-muted">
                        {timeAgo}
                      </td>
                      <td style={{ padding: '8px 6px' }}>
                        <QAStatusBadge status={change.qa_status || 'live'} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// KPI Card component
const KPICard: React.FC<{
  label: string;
  value: string | number;
  subtitle?: string;
  color: string;
  icon: React.ReactNode;
}> = ({ label, value, subtitle, color, icon }) => (
  <div className="card" style={{ padding: '16px 18px', textAlign: 'center' }}>
    <div style={{ color, marginBottom: 6, opacity: 0.8 }}>{icon}</div>
    <div style={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1.1 }}>{value}</div>
    {subtitle && (
      <div className="text-xs text-muted" style={{ marginTop: 2 }}>
        {subtitle}
      </div>
    )}
    <div className="text-xs text-muted" style={{ marginTop: 6 }}>
      {label}
    </div>
  </div>
);

// Time ago helper
function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Gerade eben';
  if (mins < 60) return `vor ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `vor ${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `vor ${days}d`;
}
